import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  Paper,
  Divider,
  CircularProgress,
  Chip,
  InputAdornment,
  Fade,
  Grow,
  Popover,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  MoreVert as MoreVertIcon,
  Minimize as MinimizeIcon,
  Lock as LockIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import messageService from '../services/messageService';
import websocketService from '../services/websocketService';
import { getPresenceBadgeColorHex, buildPresenceTooltipFromPresence } from '../utils/presence';

const MessageConversation = ({ conversation, open, onClose, onMessageSent, onArchived, embedded = false }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const messageInputRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [reactionMenu, setReactionMenu] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [updatingGroupName, setUpdatingGroupName] = useState(false);

  const COMMON_EMOJIS = ['😀','😅','😂','😉','😍','🤔','👍','🙏','🎉','❤️','🔥','✨','👏','😎','🥳','🤝'];
  const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '😡', '🎉', '🔥'];

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const sizes = ['B','KB','MB','GB','TB'];
    const i = bytes === 0 ? 0 : Math.floor(Math.log(bytes) / Math.log(1024));
    const val = (bytes / Math.pow(1024, i)).toFixed(1);
    return `${val} ${sizes[i]}`;
  };

  // Group reactions by emoji with counts
  const groupReactions = (reactions) => {
    const map = new Map();
    (reactions || []).forEach(r => {
      const emoji = r && r.emoji;
      if (!emoji) return;
      map.set(emoji, (map.get(emoji) || 0) + 1);
    });
    return Array.from(map.entries()).map(([emoji, count]) => ({ emoji, count }));
  };

  const renderReactionOverlay = (message, isOwnMessage, compact = false) => {
    const grouped = groupReactions(message?.reactions);
    if (!grouped || grouped.length === 0) return null;
    return (
      <Box sx={{ position: 'absolute', top: -6, left: isOwnMessage ? -6 : 'auto', right: isOwnMessage ? 'auto' : -6, zIndex: 2 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: compact ? 0.5 : 0.75, py: compact ? 0.2 : 0.25, borderRadius: '12px', backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: 1, fontSize: compact ? '0.65rem' : '0.75rem', lineHeight: 1 }}>
          {grouped.map((g, idx) => (
            <span key={idx} style={{ display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ marginRight: g.count > 1 ? 2 : 0 }}>{g.emoji}</span>
              {g.count > 1 ? <span style={{ opacity: 0.8 }}>{g.count}</span> : null}
            </span>
          ))}
        </Box>
      </Box>
    );
  };

  // Load messages when conversation changes
  useEffect(() => {
    if (conversation && open) {
      loadMessages();
      markAsRead();
      
      // Ensure WebSocket connection and subscribe to real-time events
      websocketService.connect().then(() => {
        console.log(`MessageConversation: Subscribing to conversation ${conversation._id}`);
        // Subscribe to conversation-specific events
        websocketService.subscribe(`conversation:${conversation._id}`);
        websocketService.addEventListener('new_message', handleNewMessage);
        websocketService.addEventListener('message_deleted', handleMessageDeleted);
        websocketService.addEventListener('typing_status', handleTypingStatus);
        websocketService.addEventListener('reaction_added', handleReactionAdded);
      }).catch(err => {
        console.warn('MessageConversation: WebSocket connect failed:', err);
      });
      
      return () => {
        websocketService.unsubscribe(`conversation:${conversation._id}`);
        websocketService.removeEventListener('new_message', handleNewMessage);
        websocketService.removeEventListener('message_deleted', handleMessageDeleted);
        websocketService.removeEventListener('typing_status', handleTypingStatus);
        websocketService.removeEventListener('reaction_added', handleReactionAdded);
      };
    }
  }, [conversation, open]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load messages
  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await messageService.getMessages(conversation._id);
      if (response.success) {
        setMessages(response.messages);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark messages as read
  const markAsRead = async () => {
    try {
      await messageService.markAsRead(conversation._id);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Handle new message from WebSocket
  const handleNewMessage = (data) => {
    const message = data.data;
    if (message.conversation._id === conversation._id) {
      setMessages(prev => [...prev, message]);
      markAsRead();
    }
  };

  // Handle message deleted
  const handleMessageDeleted = (data) => {
    const { messageId, conversationId } = data.data;
    if (conversationId === conversation._id) {
      setMessages(prev => 
        prev.map(msg => 
          msg._id === messageId 
            ? { ...msg, content: '[Message deleted]', deletedAt: new Date() }
            : msg
        )
      );
    }
  };

  // Handle typing status
  const handleTypingStatus = (data) => {
    const { conversationId, userId, isTyping, userName } = data.data;
    if (conversationId === conversation._id && userId !== user._id) {
      setTypingUsers(prev => {
        if (isTyping) {
          if (!prev.find(u => u.userId === userId)) {
            return [...prev, { userId, userName }];
          }
          return prev;
        } else {
          return prev.filter(u => u.userId !== userId);
        }
      });
    }
  };

  // Handle reaction added (real-time)
  const handleReactionAdded = (data) => {
    const { conversationId, messageId, userId, emoji } = data.data || {};
    if (!conversationId || conversationId !== conversation._id) return;
    setMessages(prev => prev.map(msg => {
      if (msg._id !== messageId) return msg;
      const existing = Array.isArray(msg.reactions) ? msg.reactions : [];
      const filtered = existing.filter(r => (r.user?._id || r.user)?.toString() !== userId);
      return { ...msg, reactions: [...filtered, { user: userId, emoji }] };
    }));
  };

  // Send message
  const sendMessage = async () => {
    if (!messageText.trim() && !pendingAttachment && !replyingTo) return;
    
    setSending(true);
    try {
      const response = await messageService.sendMessage(
        conversation._id,
        messageText.trim(),
        'text',
        replyingTo?._id,
        pendingAttachment || null
      );
      
      if (response.success) {
        setMessages(prev => [...prev, response.message]);
        setMessageText('');
        setReplyingTo(null);
        setPendingAttachment(null);
        onMessageSent && onMessageSent();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Handle typing
  const handleTyping = () => {
    if (!lastTypingRef.current) {
      messageService.setTypingStatus(conversation._id, true);
      lastTypingRef.current = true;
    }
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      messageService.setTypingStatus(conversation._id, false);
      lastTypingRef.current = false;
    }, 2000);
  };

  // Attachment handling
  const handleAttachClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const res = await messageService.uploadAttachment(file);
      if (res && res.success) {
        setPendingAttachment(res.file);
      }
    } catch (err) {
      console.error('Attachment upload failed:', err);
    } finally {
      setUploadingAttachment(false);
      // reset input so selecting the same file again triggers change
      e.target.value = '';
    }
  };

  const clearAttachment = () => setPendingAttachment(null);

  // Emoji handling
  const toggleEmojiPopover = (e) => {
    setEmojiAnchorEl(emojiAnchorEl ? null : e.currentTarget);
  };

  const handleEmojiSelect = (emoji) => {
    const el = messageInputRef.current;
    if (!el) {
      setMessageText((prev) => prev + emoji);
    } else {
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;
      const value = messageText || '';
      const next = value.slice(0, start) + emoji + value.slice(end);
      setMessageText(next);
      // move cursor after emoji on next tick
      requestAnimationFrame(() => {
        try {
          el.focus();
          const pos = start + emoji.length;
          el.setSelectionRange(pos, pos);
        } catch {}
      });
    }
    setEmojiAnchorEl(null);
  };

  // Delete message
  const deleteMessage = async (messageId) => {
    try {
      await messageService.deleteMessage(messageId);
      setMessages(prev => 
        prev.map(msg => 
          msg._id === messageId 
            ? { ...msg, content: '[Message deleted]', deletedAt: new Date() }
            : msg
        )
      );
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Edit message
  const editMessage = async (messageId, newContent) => {
    try {
      const response = await messageService.editMessage(messageId, newContent);
      if (response.success) {
        setMessages(prev => 
          prev.map(msg => 
            msg._id === messageId 
              ? { ...msg, content: newContent, editedAt: new Date() }
              : msg
          )
        );
      }
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  // Add reaction to message (optimistic update; server will echo via WS)
  const addReaction = async (messageId, emoji) => {
    try {
      // Optimistically update local state: replace prior reaction by current user, then add new
      setMessages(prev => prev.map(msg => {
        if (msg._id !== messageId) return msg;
        const currentUserId = user?._id;
        const existing = Array.isArray(msg.reactions) ? msg.reactions : [];
        const filtered = existing.filter(r => (r.user?._id || r.user)?.toString() !== currentUserId);
        return { ...msg, reactions: [...filtered, { user: currentUserId, emoji }] };
      }));

      const response = await messageService.addReaction(messageId, emoji);
      if (!response?.success) {
        // Rollback not implemented; rely on WS to correct state if needed
        console.warn('addReaction API did not succeed');
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  // Handle context menu
  const handleContextMenu = (event, message) => {
    event.preventDefault();
    const now = new Date();
    const messageTime = new Date(message.createdAt);
    const isWithin5Minutes = (now - messageTime) < 5 * 60 * 1000; // 5 minutes in milliseconds
    const isOwnMessage = message.sender._id === user._id;
    
    if (isOwnMessage && isWithin5Minutes && !message.deletedAt) {
      setSelectedMessage(message);
      setContextMenu({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      });
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedMessage(null);
  };

  const handleEditClick = () => {
    if (selectedMessage) {
      setEditingMessage(selectedMessage);
      setEditText(selectedMessage.content);
      handleCloseContextMenu();
    }
  };

  const handleDeleteClick = () => {
    if (selectedMessage) {
      deleteMessage(selectedMessage._id);
      handleCloseContextMenu();
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  const handleSaveEdit = () => {
    if (editingMessage && editText.trim()) {
      editMessage(editingMessage._id, editText.trim());
      setEditingMessage(null);
      setEditText('');
    }
  };

  // Handle message click for reactions
  const handleMessageClick = (event, message) => {
    if (!message.deletedAt) {
      setSelectedMessage(message);
      setReactionMenu({
        mouseX: event.clientX,
        mouseY: event.clientY - 40,
      });
    }
  };

  const handleReactionSelect = (emoji) => {
    if (selectedMessage) {
      addReaction(selectedMessage._id, emoji);
    }
    setReactionMenu(null);
    setSelectedMessage(null);
  };

  const handleCloseReactionMenu = () => {
    setReactionMenu(null);
    setSelectedMessage(null);
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle group name edit
  const handleStartEditGroupName = () => {
    setNewGroupName(displayName === 'Unnamed Group' ? '' : displayName);
    setEditingGroupName(true);
  };

  const handleCancelEditGroupName = () => {
    setEditingGroupName(false);
    setNewGroupName('');
  };

  const handleSaveGroupName = async () => {
    if (!newGroupName.trim()) {
      handleCancelEditGroupName();
      return;
    }

    setUpdatingGroupName(true);
    try {
      const response = await messageService.updateConversation(conversation._id, {
        groupName: newGroupName.trim()
      });

      if (response.success) {
        // Update the conversation object to reflect the new name
        conversation.groupName = newGroupName.trim();
        setEditingGroupName(false);
        setNewGroupName('');
      }
    } catch (error) {
      console.error('Error updating group name:', error);
    } finally {
      setUpdatingGroupName(false);
    }
  };

  // Get conversation display info
  const displayName = messageService.getConversationDisplayName(conversation, user._id);
  const avatar = messageService.getConversationAvatar(conversation, user._id);
  const otherParticipant = conversation && !conversation.isGroup
    ? (conversation.participants || []).find(p => p._id !== user._id)
    : null;
  const [otherPresence, setOtherPresence] = useState(null);

  useEffect(() => {
    const handlePresence = (payload) => {
      if (!payload || !payload.userId) return;
      if (otherParticipant && payload.userId === otherParticipant._id) {
        setOtherPresence(prev => ({ ...(prev || {}), ...(payload.presence || {}) }));
      }
    };
    const handleUserOnlineStatus = (payload) => {
      if (!payload || !payload.userId) return;
      if (otherParticipant && payload.userId === otherParticipant._id) {
        setOtherPresence(prev => ({
          ...(prev || {}),
          isOnline: payload.isOnline,
          status: payload.status || (prev && prev.status),
          statusMessage: payload.statusMessage || (prev && prev.statusMessage),
          lastSeenAt: payload.lastSeenAt || (prev && prev.lastSeenAt)
        }));
      }
    };

    websocketService.addEventListener('presence', handlePresence);
    websocketService.addEventListener('userOnlineStatus', handleUserOnlineStatus);
    return () => {
      websocketService.removeEventListener('presence', handlePresence);
      websocketService.removeEventListener('userOnlineStatus', handleUserOnlineStatus);
    };
  }, [otherParticipant && otherParticipant._id]);

  // Group messages by sender for better display
  const groupedMessages = messages.reduce((groups, message, index) => {
    const prevMessage = messages[index - 1];
    const isNewGroup = !prevMessage || 
      prevMessage.sender._id !== message.sender._id ||
      new Date(message.createdAt) - new Date(prevMessage.createdAt) > 60000; // 1 minute gap
    
    if (isNewGroup) {
      groups.push([message]);
    } else {
      groups[groups.length - 1].push(message);
    }
    
    return groups;
  }, []);

  if (embedded) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Embedded Header */}
        <Box sx={{ 
          p: 1.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #2563eb, #1e40af)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', mr: 1 }}>
                <Avatar
                  src={avatar ? `/api/auth/avatar-proxy?url=${encodeURIComponent(avatar)}` : undefined}
                  sx={{ width: 24, height: 24, backgroundColor: 'primary.dark' }}
                >
                  {((displayName || '').charAt(0).toUpperCase() || '?')}
                </Avatar>
                {!conversation.isGroup && (
                  <Box
                    title={buildPresenceTooltipFromPresence(otherPresence)}
                    sx={{
                      position: 'absolute',
                      right: -2,
                      bottom: -2,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: '2px solid #1e293b',
                      backgroundColor: getPresenceBadgeColorHex(otherPresence)
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {editingGroupName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <TextField
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveGroupName();
                        } else if (e.key === 'Escape') {
                          handleCancelEditGroupName();
                        }
                      }}
                      size="small"
                      autoFocus
                      disabled={updatingGroupName}
                      placeholder="Enter group name"
                      sx={{
                        width: '150px',
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          fontSize: '0.8rem',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'white',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: 'white',
                          padding: '4px 8px',
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleSaveGroupName}
                      disabled={updatingGroupName}
                      sx={{ color: 'white', p: 0.25 }}
                    >
                      {updatingGroupName ? <CircularProgress size={12} sx={{ color: 'white' }} /> : <SendIcon sx={{ fontSize: '0.75rem' }} />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEditGroupName}
                      disabled={updatingGroupName}
                      sx={{ color: 'white', p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: '0.75rem' }} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                      {displayName}
                    </Typography>
                    {conversation.isGroup && (
                      <Tooltip title="Rename group" arrow>
                        <IconButton
                          size="small"
                          onClick={handleStartEditGroupName}
                          sx={{ color: 'rgba(255, 255, 255, 0.9)', p: 0.25 }}
                        >
                          <EditIcon sx={{ fontSize: '0.65rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="End-to-end encrypted" arrow>
                      <LockIcon titleAccess="End-to-end encrypted" aria-label="End-to-end encrypted" sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)' }} />
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
              <MinimizeIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Embedded Messages */}
        <Box sx={{ flex: 1, p: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, flex: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: 'auto', mb: 1 }}>
              {groupedMessages.map((group, groupIndex) => {
                const isOwnMessage = group[0].sender._id === user._id;
                
                return (
                  <Box
                    key={groupIndex}
                    sx={{
                      display: 'flex',
                      flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                      mb: 1,
                      alignItems: 'flex-end'
                    }}
                  >
                    {!isOwnMessage && (
                      <Avatar
                        src={group[0].sender.avatar ? 
                          `/api/auth/avatar-proxy?url=${encodeURIComponent(group[0].sender.avatar)}` : 
                          undefined
                        }
                        sx={{ width: 20, height: 20, mr: 0.5, fontSize: '0.7rem' }}
                      >
                        {((group?.[0]?.sender?.name || '').charAt(0).toUpperCase() || '?')}
                      </Avatar>
                    )}
                    
                    <Box sx={{ maxWidth: '85%' }}>
                      {group.map((message, messageIndex) => (
                        <Paper
                          key={message._id}
                          onClick={(e) => handleMessageClick(e, message)}
                          onContextMenu={(e) => handleContextMenu(e, message)}
                          sx={{
                            p: 0.75,
                            pt: 1,
                            mb: 0.25,
                            borderRadius: '8px',
                            backgroundColor: isOwnMessage ? '#2563eb' : '#ffffff',
                            color: isOwnMessage ? 'white' : '#111827',
                            '& .MuiTypography-root': { color: 'inherit' },
                            border: isOwnMessage ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            position: 'relative',
                            '&:hover': {
                              backgroundColor: isOwnMessage ? '#1e40af' : '#f3f4f6'
                            }
                          }}
                        >
                          {renderReactionOverlay(message, isOwnMessage, true)}
                          {/* Attachment rendering (embedded) */}
                          {message.attachment && (message.attachment.type || '').startsWith('image/') && (
                            <Box
                              component="img"
                              src={message.attachment.url}
                              alt={message.attachment.name || 'image'}
                              sx={{
                                maxWidth: '100%',
                                borderRadius: '6px',
                                mb: message.content ? 0.5 : 0
                              }}
                            />
                          )}
                          {message.attachment && !(message.attachment.type || '').startsWith('image/') && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)', p: 0.5, borderRadius: '6px', mb: message.content ? 0.5 : 0 }}>
                              <AttachFileIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <a href={message.attachment.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontSize: '0.78rem' }}>
                                {message.attachment.name || 'Attachment'}
                              </a>
                            </Box>
                          )}
                          {message.content && (
                            <Box>
                              {editingMessage && editingMessage._id === message._id ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <TextField
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    size="small"
                                    multiline
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '0.8rem',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                      }
                                    }}
                                  />
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton size="small" onClick={handleSaveEdit}>
                                      <SendIcon sx={{ fontSize: '0.7rem' }} />
                                    </IconButton>
                                    <IconButton size="small" onClick={handleCancelEdit}>
                                      <CloseIcon sx={{ fontSize: '0.7rem' }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ) : (
                                <Box>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {message.content}
                                    {message.editedAt && (
                                      <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.7, fontSize: '0.65rem' }}>
                                        (edited)
                                      </Typography>
                                    )}
                                  </Typography>
                                  {/* Reactions overlay rendered above bubble */}
                                </Box>
                              )}
                            </Box>
                          )}
                          {messageIndex === group.length - 1 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                mt: 0.25,
                                opacity: 0.7,
                                fontSize: '0.65rem'
                              }}
                            >
                              {messageService.formatMessageTime(message.createdAt)}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>
          )}

          {/* Embedded Pending attachment preview */}
          {pendingAttachment && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
              {(pendingAttachment.type || '').startsWith('image/') ? (
                <Box component="img" src={pendingAttachment.url} alt={pendingAttachment.name || 'image'} sx={{ height: 40, borderRadius: '6px' }} />
              ) : (
                <Chip size="small" icon={<AttachFileIcon />} label={`${pendingAttachment.name || 'Attachment'}`} />
              )}
              <IconButton size="small" onClick={clearAttachment}><CloseIcon fontSize="small" /></IconButton>
            </Box>
          )}

          {/* Embedded Message input */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={messageText}
              inputRef={messageInputRef}
              onChange={(e) => {
                setMessageText(e.target.value);
                handleTyping();
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={sending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  fontSize: '0.8rem'
                },
                '& .MuiInputBase-input': {
                  padding: '8px 12px'
                }
              }}
            />
            <IconButton size="small" onClick={toggleEmojiPopover}>
              <EmojiIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleAttachClick} disabled={uploadingAttachment}>
              {uploadingAttachment ? <CircularProgress size={14} /> : <AttachFileIcon fontSize="small" />}
            </IconButton>
            <IconButton
              onClick={sendMessage}
              disabled={(!messageText.trim() && !pendingAttachment) || sending}
              size="small"
              sx={{
                backgroundColor: (messageText.trim() || pendingAttachment) ? '#2563eb' : 'transparent',
                color: (messageText.trim() || pendingAttachment) ? 'white' : 'inherit',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: (messageText.trim() || pendingAttachment) ? '#1e40af' : 'transparent'
                }
              }}
            >
              {sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
            </IconButton>
            {/* hidden input and popover for embedded */}
            <input ref={fileInputRef} type="file" onChange={handleFileChange} hidden />
            <Popover
              open={Boolean(emojiAnchorEl)}
              anchorEl={emojiAnchorEl}
              onClose={() => setEmojiAnchorEl(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              sx={{ zIndex: 1700 }}
            >
              <Box sx={{ p: 1, maxWidth: 240, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
                {COMMON_EMOJIS.map((e) => (
                  <IconButton key={e} size="small" onClick={() => handleEmojiSelect(e)}>
                    <span style={{ fontSize: 18 }}>{e}</span>
                  </IconButton>
                ))}
              </Box>
            </Popover>
          </Box>

          {/* Context Menu for embedded right-click actions */}
          <Menu
            open={contextMenu !== null}
            onClose={handleCloseContextMenu}
            anchorReference="anchorPosition"
            anchorPosition={
              contextMenu !== null
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
            }
            sx={{ zIndex: 1800 }}
          >
            <MenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Message</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteClick}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete Message</ListItemText>
            </MenuItem>
          </Menu>

          {/* Reaction Menu for embedded left-click actions */}
          <Menu
            open={reactionMenu !== null}
            onClose={handleCloseReactionMenu}
            anchorReference="anchorPosition"
            anchorPosition={
              reactionMenu !== null
                ? { top: reactionMenu.mouseY, left: reactionMenu.mouseX }
                : undefined
            }
            sx={{ zIndex: 1800 }}
          >
            <Box sx={{ p: 0.5, display: 'flex', gap: 0.25 }}>
              {REACTION_EMOJIS.map((emoji) => (
                <IconButton
                  key={emoji}
                  size="small"
                  onClick={() => handleReactionSelect(emoji)}
                  sx={{ 
                    padding: 0.25,
                    '&:hover': { 
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      transform: 'scale(1.2)'
                    },
                    transition: 'transform 0.1s ease'
                  }}
                >
                  <span style={{ fontSize: 16 }}>{emoji}</span>
                </IconButton>
              ))}
            </Box>
          </Menu>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        background: 'linear-gradient(135deg, #2563eb, #1e40af)',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ position: 'relative', mr: 2 }}>
              <Avatar
                src={avatar ? `/api/auth/avatar-proxy?url=${encodeURIComponent(avatar)}` : undefined}
                sx={{ backgroundColor: 'primary.dark' }}
              >
                {((displayName || '').charAt(0).toUpperCase() || '?')}
              </Avatar>
              {!conversation.isGroup && (
                <Box 
                  title={buildPresenceTooltipFromPresence(otherPresence)}
                  sx={{
                    position: 'absolute',
                    right: -2,
                    bottom: -2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    border: '2px solid #1e40af',
                    backgroundColor: getPresenceBadgeColorHex(otherPresence)
                  }} 
                />
              )}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {editingGroupName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveGroupName();
                        } else if (e.key === 'Escape') {
                          handleCancelEditGroupName();
                        }
                      }}
                      size="small"
                      autoFocus
                      disabled={updatingGroupName}
                      placeholder="Enter group name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'white',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: 'white',
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleSaveGroupName}
                      disabled={updatingGroupName}
                      sx={{ color: 'white' }}
                    >
                      {updatingGroupName ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SendIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEditGroupName}
                      disabled={updatingGroupName}
                      sx={{ color: 'white' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ color: 'white' }}>{displayName}</Typography>
                    {conversation.isGroup && (
                      <Tooltip title="Rename group" arrow>
                        <IconButton
                          size="small"
                          onClick={handleStartEditGroupName}
                          sx={{ color: 'rgba(255, 255, 255, 0.9)', p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="End-to-end encrypted" arrow>
                      <LockIcon titleAccess="End-to-end encrypted" aria-label="End-to-end encrypted" sx={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.9)' }} />
                    </Tooltip>
                  </>
                )}
              </Box>
              {typingUsers.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {typingUsers.map(u => u.userName).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Messages */}
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {groupedMessages.map((group, groupIndex) => {
              const isOwnMessage = group[0].sender._id === user._id;
              
              return (
                <Fade in key={groupIndex} timeout={300}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                      mb: 2,
                      alignItems: 'flex-end'
                    }}
                  >
                    {!isOwnMessage && (
                      <Avatar
                        src={group[0].sender.avatar ? 
                          `/api/auth/avatar-proxy?url=${encodeURIComponent(group[0].sender.avatar)}` : 
                          undefined
                        }
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 1,
                          mb: 0.5
                        }}
                      >
                        {((group?.[0]?.sender?.name || '').charAt(0).toUpperCase() || '?')}
                      </Avatar>
                    )}
                    
                    <Box sx={{ maxWidth: '70%' }}>
                      {group.map((message, messageIndex) => (
                        <Grow in key={message._id} timeout={200}>
                          <Paper
                            onClick={(e) => handleMessageClick(e, message)}
                            onContextMenu={(e) => handleContextMenu(e, message)}
                            sx={{
                              p: 1.5,
                              pt: 1.75,
                              mb: 0.5,
                              borderRadius: '12px',
                              backgroundColor: isOwnMessage 
                                ? 'linear-gradient(135deg, #2563eb, #1e40af)'
                                : 'rgba(0, 0, 0, 0.05)',
                              background: isOwnMessage 
                                ? 'linear-gradient(135deg, #2563eb, #1e40af)'
                                : 'rgba(0, 0, 0, 0.05)',
                              color: isOwnMessage ? 'white' : 'inherit',
                              '& .MuiTypography-root': { color: 'inherit' },
                              position: 'relative',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: !isOwnMessage ? 'rgba(0, 0, 0, 0.08)' : undefined,
                                background: isOwnMessage ? 'linear-gradient(135deg, #1e40af, #1e3a8a)' : undefined
                              },
                              '&:hover .message-actions': {
                                opacity: 1
                              }
                            }}
                          >
                            {renderReactionOverlay(message, isOwnMessage, false)}
                            {/* Reply reference */}
                            {message.replyTo && (
                              <Box sx={{ 
                                mb: 1, 
                                p: 1, 
                                borderLeft: '3px solid',
                                borderColor: isOwnMessage ? 'rgba(255, 255, 255, 0.5)' : 'rgba(37, 99, 235, 0.5)',
                                backgroundColor: isOwnMessage ? 'rgba(255, 255, 255, 0.1)' : 'rgba(37, 99, 235, 0.1)',
                                borderRadius: '4px'
                              }}>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                  Replying to {message.replyTo.sender.name}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                  {message.replyTo.content}
                                </Typography>
                              </Box>
                            )}
                            
                            {/* Message content */}
                            {message.attachment && (message.attachment.type || '').startsWith('image/') && (
                              <Box
                                component="img"
                                src={message.attachment.url}
                                alt={message.attachment.name || 'image'}
                                sx={{
                                  maxWidth: '100%',
                                  borderRadius: '8px',
                                  mb: message.content ? 1 : 0
                                }}
                              />
                            )}
                            {message.attachment && !(message.attachment.type || '').startsWith('image/') && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  backgroundColor: isOwnMessage ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                                  p: 1,
                                  borderRadius: '8px',
                                  mb: message.content ? 1 : 0
                                }}
                              >
                                <AttachFileIcon fontSize="small" sx={{ mr: 0.5 }} />
                                <a href={message.attachment.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                                  {message.attachment.name || 'Attachment'}
                                </a>
                                {message.attachment.size ? (
                                  <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                                    {formatBytes(message.attachment.size)}
                                  </Typography>
                                ) : null}
                              </Box>
                            )}
                            {message.content && (
                              <Box>
                                {editingMessage && editingMessage._id === message._id ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField
                                      value={editText}
                                      onChange={(e) => setEditText(e.target.value)}
                                      multiline
                                      maxRows={4}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                        }
                                      }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <IconButton size="small" onClick={handleSaveEdit} sx={{ color: 'inherit' }}>
                                        <SendIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={handleCancelEdit} sx={{ color: 'inherit' }}>
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box>
                                    <Typography variant="body1">
                                      {message.content}
                                      {message.editedAt && (
                                        <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                                          (edited)
                                        </Typography>
                                      )}
                                    </Typography>
                                    {/* Reactions overlay rendered above bubble */}
                                  </Box>
                                )}
                              </Box>
                            )}
                            
                            {/* Timestamp */}
                            {messageIndex === group.length - 1 && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block',
                                  mt: 0.5,
                                  opacity: 0.7,
                                  fontSize: '0.75rem'
                                }}
                              >
                                {messageService.formatMessageTime(message.createdAt)}
                              </Typography>
                            )}
                            
                            {/* Message actions */}
                            <Box 
                              className="message-actions"
                              sx={{ 
                                position: 'absolute',
                                top: -20,
                                right: isOwnMessage ? 0 : 'auto',
                                left: isOwnMessage ? 'auto' : 0,
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                display: 'flex',
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => setReplyingTo(message)}
                                sx={{ p: 0.5 }}
                              >
                                <ReplyIcon fontSize="small" />
                              </IconButton>
                              {isOwnMessage && !message.deletedAt && (
                                <IconButton
                                  size="small"
                                  onClick={() => deleteMessage(message._id)}
                                  sx={{ p: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Paper>
                        </Grow>
                      ))}
                    </Box>
                  </Box>
                </Fade>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}

        {/* Reply indicator */}
        {replyingTo && (
          <Paper sx={{ 
            p: 1.5, 
            mb: 1,
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            borderLeft: '3px solid #2563eb'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" color="primary">
                  Replying to {replyingTo.sender.name}
                </Typography>
                <Typography variant="body2">
                  {replyingTo.content}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyingTo(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        {/* Pending attachment preview */}
        {pendingAttachment && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
            {(pendingAttachment.type || '').startsWith('image/') ? (
              <Box component="img" src={pendingAttachment.url} alt={pendingAttachment.name || 'image'} sx={{ height: 56, borderRadius: '8px' }} />
            ) : (
              <Chip icon={<AttachFileIcon />} label={`${pendingAttachment.name || 'Attachment'}${pendingAttachment.size ? ' • ' + formatBytes(pendingAttachment.size) : ''}`} />
            )}
            <IconButton size="small" onClick={clearAttachment}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        )}

        {/* Message input */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', mt: 2 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message..."
            value={messageText}
            inputRef={messageInputRef}
            onChange={(e) => {
              setMessageText(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            disabled={sending}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggleEmojiPopover} sx={{ mr: 1 }}>
                    <EmojiIcon />
                  </IconButton>
                  <IconButton onClick={handleAttachClick} sx={{ mr: 1 }} disabled={uploadingAttachment}>
                    {uploadingAttachment ? <CircularProgress size={18} /> : <AttachFileIcon />}
                  </IconButton>
                  <IconButton
                    onClick={sendMessage}
                    disabled={(!messageText.trim() && !pendingAttachment) || sending}
                    sx={{
                      background: (messageText.trim() || pendingAttachment) ? 'linear-gradient(135deg, #2563eb, #1e40af)' : 'transparent',
                      color: (messageText.trim() || pendingAttachment) ? 'white' : 'inherit',
                      '&:hover': {
                        background: (messageText.trim() || pendingAttachment) ? 'linear-gradient(135deg, #1e40af, #1e3a8a)' : 'transparent'
                      }
                    }}
                  >
                    {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {/* hidden file input and emoji popover */}
          <input ref={fileInputRef} type="file" onChange={handleFileChange} hidden />
          <Popover
            open={Boolean(emojiAnchorEl)}
            anchorEl={emojiAnchorEl}
            onClose={() => setEmojiAnchorEl(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{ zIndex: 1700 }}
          >
            <Box sx={{ p: 1, maxWidth: 240, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
              {COMMON_EMOJIS.map((e) => (
                <IconButton key={e} size="small" onClick={() => handleEmojiSelect(e)}>
                  <span style={{ fontSize: 18 }}>{e}</span>
                </IconButton>
              ))}
            </Box>
          </Popover>
        </Box>

        {/* Context Menu for right-click actions */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={handleEditClick}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Message</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Message</ListItemText>
          </MenuItem>
        </Menu>

        {/* Reaction Menu for left-click actions */}
        <Menu
          open={reactionMenu !== null}
          onClose={handleCloseReactionMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            reactionMenu !== null
              ? { top: reactionMenu.mouseY, left: reactionMenu.mouseX }
              : undefined
          }
          sx={{ zIndex: 1700 }}
        >
          <Box sx={{ p: 1, display: 'flex', gap: 0.5 }}>
            {REACTION_EMOJIS.map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                onClick={() => handleReactionSelect(emoji)}
                sx={{ 
                  '&:hover': { 
                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                    transform: 'scale(1.2)'
                  },
                  transition: 'transform 0.1s ease'
                }}
              >
                <span style={{ fontSize: 20 }}>{emoji}</span>
              </IconButton>
            ))}
          </Box>
        </Menu>
      </DialogContent>
    </Dialog>
  );
};

export default MessageConversation;